<script lang="ts">
  import RetailerInviteIndex from "@modern/components/distributor/retailer_onboarding/RetailerInviteIndex.svelte";
  import RROForm from "@shared/svelte/Components/RROForm.svelte";
  import Spinner from "@shared/svelte/Components/Spinner.svelte";

  import Flash from "@shared/services/flashv2.js";
  import { HttpPromise, HttpPromiseWithEntireResponse } from "@shared/svelte/Modules/ApiService";
  import { params } from "@roxi/routify";

  import { DistributorStore } from "@distributor/svelte/Stores/DistributorStore";
  import type { DistributorUser } from "@shared/types/Distributor";

  let account = null;
  let accountNumber: string = "";
  let distributor: DistributorUser | null = $DistributorStore.currentDistributor;
  let retailerLookupFailure: boolean = false;
  let submittingData: boolean = false;

  const fetchReferralCode = async (): Promise<{ code: string }> => {
    return await HttpPromise({
      url: "/api/distributor/referral_code",
      method: "GET",
    });
  };

  const handleLookupFailure = () => {
    retailerLookupFailure = true;
    account = {
      state: distributor.state,
      distributor: distributor,
    };
  };

  const lookUpAccount = async () => {
    try {
      const res = await HttpPromise({
        method: "GET",
        url: "/api/distributor/integrated_distributor_accounts",
        data: {
          account_number: accountNumber,
        },
      });
      account = res[0];

      if (account) {
        account.sales_reps = account.integrated_sales_reps;
        account.retailer_user = account.connected_retailer?.retailer_users[0];
        account.distributor = distributor;
        account.license_type = account.off_premises == true ? "off_premise" : "on_premise";
      } else {
        handleLookupFailure();
      }
    } catch (e) {
      retailerLookupFailure = true;
      handleLookupFailure();
    }
  };

  const runOnboarding = async (retailer, retailerUser, selectedReps) => {
    submittingData = true;

    try {
      await HttpPromiseWithEntireResponse({
        method: "POST",
        url: "/api/distributor/integrated_distributor_accounts/onboard_retailer",
        data: {
          onboard_retailer: { ...retailer, reps: selectedReps || [], user: retailerUser },
        },
      });
      submittingData = false;
      Flash.now({
        type: "success",
        message: "Retailer onboarded successfully.",
      });
      window.location.href = "/distributor/retailer_onboarding?success=true";
    } catch (error) {
      submittingData = false;
      Flash.now({
        type: "error",
        message: error?.errors?.messages[0] || "There was an error onboarding the retailer.",
      });
    }
  };
</script>

{#await fetchReferralCode()}
  <Spinner />
{:then referralCode}
  <div class="fizz-styles">
    <div class="fizz-surface fizz-inset-square-12 fizz-space-between">
      <h5 class="fizz-heading-5">Onboard Retailers</h5>
      <div class="fizz-flex-row-4">
        <p class="fizz-text-large">Your Invite Code:</p>
        <h5 class="fizz-heading-5 uppercase">{referralCode?.code}</h5>
      </div>
    </div>

    <div class="refer-and-earn-header onboard-header align-center background-brandSecondary">
      <h1 class="display-inline-block s2 color-white position-relative" style="top: 0;"
        >Onboard Retailers to Provi</h1
      >
    </div>

    <div class="refer-and-earn-wrapper background-white position-relative">
      <div class="padding--medium">
        <div class="align-center">
          {#if $params.success && !accountNumber}
            <div class="retailer-onboard-success-message">
              <div class="s3 strong">All Set!</div>
              <div class="s3">1. Ask retailer to check their inbox for Provi login info</div>
              <div class="s3">2. Guide them to "Lists" in upper right hand corner</div>
              <div class="s3">3. Add products and check out</div>
            </div>
          {/if}

          <h4 class="fizz-heading-4">Enter the customer's account number to get started</h4>
        </div>
        <div class="refer-and-earn-form">
          <div class="grid-row">
            <div class="grid-col c6-12--md c12-12">
              <div class="label input__label--left margin-top--small padding-bottom--smallest"
                >Retailer Account Number</div
              >
              <div class="input-wrapper full-width">
                <input
                  bind:value={accountNumber}
                  class="input"
                  data-testid="retailer-account-number"
                  disabled={submittingData}
                  type="text"
                />
              </div>
            </div>
            <div class="grid-col c6-12--md c12-12">
              <div class="padding-top--smallest show--md"><br /></div>
              <button
                class="button button--primary full-width margin-top--small"
                data-testid="lookup-account-button"
                on:click={lookUpAccount}>Find Account</button
              >
            </div>
          </div>
          {#if retailerLookupFailure}
            <div class="grid-row">
              <div class="s4 color-red padding-top--small">
                That account number is wrong or missing from our system. Don't worry, you can still
                onboard this retailer below.
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>

    {#if account || retailerLookupFailure}
      <RROForm
        {account}
        {submittingData}
        {runOnboarding}
        accountNumberRequired={false}
        retailerUserRequired={true}
        validateDistributorDomain={false}
        salesRepApi={{
          url: "/api/distributor/sales_reps",
          params: input => {
            return { search: input };
          },
        }}
      />
    {/if}
    <RetailerInviteIndex />
  </div>
{/await}
